import { Form } from 'antd';
import { SkeletonFields } from '../../../../../shared/components/SkeletonFields';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import { AddableSelect } from '../components/AddableSelect';
import { useTruckMakeModelFields } from '../hooks/use-truck-make-model-fields';
import { makeOptions, modelOptions } from '../policies/catalogue-options';

/**
 * Make and model for the truck form, picked from the catalogue rather than typed. The model list follows the chosen make;
 * a user who can extend the catalogue (`configuration.manage`) is offered an add for a name that is not listed, and the server decides.
 * A value the catalogue lacks, on a truck saved before it existed, stays selected and saves unchanged. Rendered inside the truck's `Form`.
 */
export function TruckMakeModelFields() {
  const { catalogue, make, model, makeId, canExtend, changeMake, addMake, addModel } = useTruckMakeModelFields();

  if (catalogue.status === 'loading') {
    return (
      <SkeletonRegion label="Loading makes and models" className="mb-6">
        <SkeletonFields count={2} columns={2} />
      </SkeletonRegion>
    );
  }

  if (catalogue.status === 'error') {
    return (
      <p role="alert" className="mb-6 text-sm text-error">
        Couldn't load the makes and models. Reload the page to try again.
      </p>
    );
  }

  const { makes } = catalogue;
  const knownModels = makes.find((m) => m.id === makeId)?.models.map((m) => m.name) ?? [];

  return (
    <div className="grid grid-cols-2 gap-3">
      <Form.Item name="make" label="Make" rules={[{ required: true, message: 'Select a make' }]}>
        <AddableSelect
          options={makeOptions(makes, make)}
          knownNames={makes.map((m) => m.name)}
          placeholder="Select a make"
          onChange={changeMake}
          add={canExtend ? { noun: 'make', run: addMake } : undefined}
        />
      </Form.Item>
      <Form.Item name="model" label="Model" rules={[{ required: true, message: 'Select a model' }]}>
        <AddableSelect
          options={modelOptions(makes, make, model)}
          knownNames={knownModels}
          placeholder={make ? 'Select a model' : 'Select a make first'}
          disabled={!make}
          add={canExtend && makeId ? { noun: 'model', run: addModel } : undefined}
        />
      </Form.Item>
    </div>
  );
}
